import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu';
import { ThemeToggleButton, useThemeTransition } from '@components/ui/shadcn-io/theme-toggle-button';
import { getNavigation } from '@content';
import { useAuthSession } from '@hooks/use-commerce';
import { useLocale } from '@hooks/use-locale';
import { useTheme } from '@hooks/use-theme';
import { cn } from '@lib/theme';
import { Link } from '@tanstack/react-router';
import { NavigationIcon } from '@ui/NavigationIcon';
import { motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { ChevronDown, Search, UserRound } from 'lucide-react';
import { useCallback, useState } from 'react';

import { FullScreenMenu, MenuButton } from './Menu';
import { useMenuContext } from './Menu/MenuContext';

export default function Header() {
  const { scrollY } = useScroll();
  const { isOpen } = useMenuContext();
  const { theme, toggleTheme } = useTheme();
  const sessionQuery = useAuthSession();
  const { startTransition } = useThemeTransition();
  const { locale, localizeHref, basePathname } = useLocale();
  const navigationContent = getNavigation(locale);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isTamil = locale === 'ta';
  const primaryDesktopLinks = navigationContent.primary.slice(0, 5);
  const secondaryDesktopLinks = navigationContent.primary.slice(5);

  useMotionValueEvent(scrollY, 'change', (value) => {
    setIsScrolled((current) => {
      if (value > 72) return true;
      if (value < 24) return false;
      return current;
    });
  });

  const handleThemeToggle = useCallback(() => {
    startTransition(() => toggleTheme());
  }, [startTransition, toggleTheme]);

  const isActiveHref = (href: string) =>
    href === '/' ? basePathname === '/' : basePathname === href || basePathname.startsWith(`${href}/`);

  return (
    <motion.header
      layout
      transition={{ layout: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } }}
      className={cn(
        'fixed mx-auto border transition-[top,left,right,max-width,border-radius,background-color,border-color,box-shadow] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform',
        isOpen ? 'z-120' : 'z-100',
        isScrolled || isOpen
          ? 'bg-background/92 inset-x-3 top-3 max-w-[82rem] rounded-full border-[color-mix(in_srgb,var(--brand-zari)_30%,var(--border))] shadow-[0_18px_55px_-28px_color-mix(in_srgb,var(--brand-indigo)_72%,transparent)] backdrop-blur-xl sm:inset-x-5'
          : 'bg-background/96 inset-x-0 top-0 max-w-none rounded-none border-x-transparent border-t-transparent border-b-[color-mix(in_srgb,var(--brand-zari)_35%,var(--border))] backdrop-blur-md'
      )}
      role="banner"
    >
      <nav
        className={cn(
          'mx-auto flex items-center gap-3 px-4 transition-[height,padding] duration-500 sm:px-6',
          isScrolled || isOpen ? 'h-16' : 'h-20 lg:h-24',
          isScrolled || isOpen ? 'max-w-none' : 'container'
        )}
        aria-label={isTamil ? 'முதன்மை வழிசெலுத்தல்' : 'Primary'}
      >
        <Link
          to={localizeHref('/')}
          className="group flex min-w-0 items-center gap-2.5 rounded-sm focus-visible:ring-2 sm:gap-3"
          aria-label="Sree Silks home"
        >
          <motion.img
            src="/assets/optimized/primary-logo-header.webp"
            alt=""
            className={cn(
              'shrink-0 object-contain transition-[width,height] duration-500',
              isScrolled ? 'h-10 w-10' : 'h-11 w-11 sm:h-13 sm:w-13'
            )}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            width={48}
            height={48}
            whileHover={{ rotate: -4, scale: 1.04 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          />
          <span
            className={cn(
              'hidden min-w-0 overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-500 sm:block',
              isScrolled ? 'max-w-0 opacity-0' : 'max-w-64 opacity-100'
            )}
          >
            <span className="font-heading text-foreground block text-xl leading-none font-medium sm:text-2xl">
              Sree Silks
            </span>
            <span className="text-muted-foreground mt-1 block text-[0.65rem] font-semibold tracking-[0.16em] uppercase">
              {isTamil ? '1965 முதல் · ஜலகண்டபுரம்' : 'Since 1965 · Jalakandapuram'}
            </span>
          </span>
        </Link>

        <div className="ml-auto hidden h-full items-center xl:flex">
          {primaryDesktopLinks.map((item) => {
            const isActive = isActiveHref(item.href);
            return (
              <div key={item.id} className="group/nav flex h-full items-center px-2">
                <Link
                  to={localizeHref(item.href)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'relative inline-flex min-h-11 items-center px-1 text-[0.78rem] font-semibold transition-colors duration-300',
                    isTamil ? 'tracking-normal' : 'tracking-[0.12em] uppercase',
                    isActive ? 'text-foreground' : 'text-foreground/72 hover:text-foreground'
                  )}
                >
                  {item.name}
                  <span
                    className={cn(
                      'bg-brand-zari absolute right-1 bottom-1 left-1 h-px origin-left shadow-[0_0_9px_color-mix(in_srgb,var(--brand-zari)_65%,transparent)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                      isActive ? 'scale-x-100' : 'scale-x-0 group-hover/nav:scale-x-100'
                    )}
                    aria-hidden="true"
                  />
                </Link>
              </div>
            );
          })}

          {secondaryDesktopLinks.length ? (
            <DropdownMenu onOpenChange={setIsMoreMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'text-foreground/75 hover:text-foreground focus-visible:ring-ring ml-1 inline-flex min-h-11 items-center gap-1.5 px-2 text-[0.78rem] font-semibold transition-colors focus-visible:ring-2',
                    isTamil ? 'tracking-normal' : 'tracking-[0.12em] uppercase'
                  )}
                  aria-label={isTamil ? 'மேலும் இணைப்புகள்' : 'More navigation links'}
                >
                  {isTamil ? 'மேலும்' : 'More'}
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform duration-300', isMoreMenuOpen && 'rotate-180')}
                    aria-hidden="true"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={12}
                className="bg-popover border-border w-80 rounded-xl border p-2 shadow-md"
              >
                {secondaryDesktopLinks.map((item) => (
                  <DropdownMenuItem key={item.id} asChild className="focus:bg-muted rounded-lg p-0">
                    <Link
                      to={localizeHref(item.href)}
                      aria-current={isActiveHref(item.href) ? 'page' : undefined}
                      className="flex min-h-14 w-full items-start gap-3 px-3 py-3"
                    >
                      <NavigationIcon variant={item.icon} size="sm" />
                      <span>
                        <span className="text-foreground block text-sm font-semibold">{item.name}</span>
                        {item.description ? (
                          <span className="text-muted-foreground mt-0.5 block text-xs leading-relaxed">
                            {item.description}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="ml-auto flex items-center gap-1 sm:gap-1.5 xl:ml-3">
          <Link
            to={localizeHref('/search')}
            className="text-foreground hover:bg-muted hover:text-brand-peacock inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors"
            aria-label={isTamil ? 'தேடல்' : 'Search'}
          >
            <Search className="h-4.5 w-4.5" aria-hidden="true" />
          </Link>
          <ThemeToggleButton
            theme={theme}
            onClick={handleThemeToggle}
            className="text-foreground hover:bg-muted hover:text-brand-peacock inline-flex h-11 w-11 rounded-full border-0 transition-colors"
          />
          <Link
            to={sessionQuery.data?.user ? localizeHref('/account') : localizeHref('/account/sign-in')}
            className="text-foreground hover:bg-muted hover:text-brand-peacock hidden h-11 w-11 items-center justify-center rounded-full transition-colors sm:inline-flex"
            aria-label={
              sessionQuery.data?.user ? (isTamil ? 'என் கணக்கு' : 'My account') : isTamil ? 'உள்நுழை' : 'Sign in'
            }
          >
            <UserRound className="h-4.5 w-4.5" aria-hidden="true" />
          </Link>
          <Link
            to={localizeHref('/contact')}
            className="zari-hover bg-accent text-accent-foreground hover:bg-brand-zari hidden min-h-11 items-center rounded-full px-5 text-xs font-bold tracking-[0.1em] uppercase transition-colors lg:inline-flex"
          >
            {isTamil ? 'ஆலோசனை' : 'Consult us'}
          </Link>
          <MenuButton aria-controls="fullscreen-menu" aria-haspopup="dialog" className="h-11 w-11 xl:hidden">
            <span className="sr-only">
              {isOpen ? (isTamil ? 'மெனுவை மூடு' : 'Close menu') : isTamil ? 'மெனுவை திற' : 'Open menu'}
            </span>
          </MenuButton>
        </div>
      </nav>
      <FullScreenMenu />
    </motion.header>
  );
}
